import type * as models from 'pkg/api/models';
import type { Event, EventTypes } from 'pkg/api/models/event';
import type {
	AttendanceStatuses,
	EventUserStatus,
} from 'pkg/api/models/event_user';
import type { Group } from 'pkg/api/models/group';
import type { Record } from 'pkg/api/models/record';
import type { User } from 'pkg/api/models/user';
import type { ViewTable } from 'pkg/view_responses/table';

import type { TableRow } from 'components/tables/interface';

export interface GroupReport extends Record {
	groupId: number;

	attendanceRate: number;
	unhandledRate: number;
	injuredRate: number;
	injuredAmount: number;
	illnessRate: number;
	illnessAmount: number;
	otherAbsenceRate: number;

	otherAbsenceAmount: number;

	notFitToPlayRate: number;
	notFitToPlayAmount: number;
	schoolRate: number;
	schoolAmount: number;
	workRate: number;
	workAmount: number;
	travelRate: number;
	travelAmount: number;
	technicalDecisionRate: number;
	technicalDecisionAmount: number;
	suspendedRate: number;
	suspendedAmount: number;
	permissionRate: number;
	permissionAmount: number;
	playingForAnotherTeamRate: number;
	playingForAnotherTeamAmount: number;
	selectedForRepresentativeTeamRate: number;
	selectedForRepresentativeTeamAmount: number;

	totalLokEvents: number;
	approvedLokEvents: number;
	totalEventUsers: number;

	matchEventsCount: number;
	practiceEventsCount: number;
	meetingEventsCount: number;
	campEventsCount: number;
	cupEventsCount: number;
	uncategorizedEventsCount: number;

	events: Event[];
	group: Group;
}

export interface UserDetailReport extends Record {
	eventId: number;
	eventTitle: string;
	eventStart: number;
	eventEnd: number;
	userComment: string;
	groupId: number;

	eventType: EventTypes;
	userStatus: EventUserStatus;
	userAttendanceStatus: AttendanceStatuses;
	group: models.group.Group;
}

export enum AttendanceDataTypes {
	Attending = 'attending',
	NotAttending = 'not_attending',
	Unhandled = 'unhandled',
}

interface PeriodizedData {
	periodizedData: {
		datePeriods: { [key: string]: { currentPeriod: number } };
		showAs: string;
		total: { currentPeriodTotal: number };
	};
}

export interface PeriodizedEventUserData extends PeriodizedData {
	dataType: AttendanceDataTypes;
}

export enum AbsenceDataTypes {
	Sick = 'sick',
	Injured = 'injured',
	Other = 'other',
}

export interface PeriodizedAbsenceData extends PeriodizedData {
	dataType: AttendanceStatuses;
}

export interface AttendanceDashboardData {
	eventCount: number;

	attendedAmount: number;
	injuredAmount: number;
	illnessAmount: number;
	otherReasonAmount: number;
	unhandledAmount: number;
	didNotAttendAmount: number;

	attendanceRate: number;
	didNotAttendRate: number;
	unhandledRate: number;

	absenceIllnessEventCount: number;
	absenceInjuredEventCount: number;
	absenceOtherEventCount: number;

	matchEventsCount: number;
	practiceEventsCount: number;
	meetingEventsCount: number;
	campEventsCount: number;
	cupEventsCount: number;
	uncategorizedEventsCount: number;

	matchEventsRate: number;
	practiceEventsRate: number;
	meetingEventsRate: number;
	campEventsRate: number;
	cupEventsRate: number;
	uncategorizedEventsRate: number;

	periodizedEventUserData: PeriodizedEventUserData[];
	periodizedAbsenceData: PeriodizedAbsenceData[];
}

export interface ReportRow {
	id: number;
	event: models.event.Event;
	attendanceUnhandled: number;
	attendanceAttended: number;
	totalAbsent: number;
	attendanceInjured: number;
	attendanceSick: number;
	attendanceNotFitToPlay: number;
	attendanceSchool: number;
	attendanceWork: number;
	attendanceTravel: number;
	attendanceTechnicalDecision: number;
	attendanceSuspended: number;
	attendancePermission: number;
	attendancePlayingForAnotherTeam: number;
	attendanceSelectedForRepresentativeTeam: number;
	attendanceOther: number;
	invitePending: number;
	inviteAccepted: number;
	inviteDeclined: number;
	validForLok: boolean;
}

export type LOKBucket =
	| 'failed'
	| 'pending'
	| 'approved'
	| 'not_eligible'
	| 'not_reported';

export type LOKStatus =
	| 'unknown'
	| 'approved'
	| 'rejected'
	| 'validation_failed'
	| 'submitted';

export type LOKQueueStatus = 'unknown' | 'pending' | 'applied' | 'failed';

export type TSPStatus = 'unprocessed' | 'not_eligible' | 'failed';

export interface LOKEventStatus {
	eventId: number;
	activityId: string | null;
	lokStatus: LOKStatus;
	lokQueueStatus: LOKQueueStatus;
	lokError: string;
	tspStatus: TSPStatus;
	tspError: string;
	lokLastUpdatedAt: number | null;
	updatedAt: number | null;
}

export interface LOKPersonError {
	userId: number;
	firstName: string;
	lastName: string;
	lokStatus: LOKStatus;
	lokError: string;
	tspStatus: TSPStatus;
	tspError: string;
}

// A row of the report's table builder response; the typed bits are in its meta.
export type LOKReportRow = TableRow;

// Why an event can not be reported yet, empty when nothing stops it.
export type LOKReportBlocker =
	| ''
	| 'no_location'
	| 'no_valid_participants'
	| 'no_reporter'
	| 'reporter_not_valid';

export interface LOKReportRowMeta {
	bucket: LOKBucket;
	eventType: EventTypes;
	canReport: boolean;
	reportBlocker: LOKReportBlocker;
	// The first line of the row's details is the event's own error.
	hasEventError: boolean;
}

export type LOKParticipantStatus =
	| 'failed'
	| 'missing_information'
	| 'approved'
	| 'pending'
	| 'not_eligible'
	| 'not_reported'
	| 'no_issues'
	| 'not_attended'
	| 'attendance_not_reported';

// What the backend puts in the meta of each participant row of an event's LOK tab.
export interface LOKParticipantRowMeta {
	userId: number;
	leader: boolean;
	handicap: boolean;
	lokValid: boolean;
	lokError: boolean;
	status: LOKParticipantStatus;
}

// Where an event stands with LOK, for the header of its LOK tab.
export interface LOKParticipantsReporter {
	userId: number;
	name: string;
	lokValid: boolean;
	lokError: string;
}

export interface LOKParticipantsSummary {
	bucket: LOKBucket;
	bucketText: string;
	bucketColor: string;
	lastSentAt: number | null;
	error: string;
	// Ours ("tsp"), from before anything was sent, or LOK's.
	errorSource: 'tsp' | 'lok' | '';
	// Who the attendance is filed under, null while nobody has reported it.
	reporter: LOKParticipantsReporter | null;
	reportBlocker: LOKReportBlocker;
	canReport: boolean;
}

export interface LOKParticipantsResponse {
	table: ViewTable;
	summary: LOKParticipantsSummary;
}

export interface UserSummaryReport {
	userId: number;
	total: number;
	absent: number;
	attendance: { [key: string]: number };
	eventType: { [key: string]: number };
	response: { [key: string]: number };
	isValidForLok: boolean;
	user: User;
}
